<div class="routines-monitor">
  <div class="header-row">
    <h2 class="title">Skincare Routines</h2>
    <button nz-button nzType="default" (click)="refresh()">
      <span nz-icon nzType="reload"></span> Refresh
    </button>
  </div>

  <!-- Queue status badges -->
  <div class="status-cards">
    @for (entry of queueStatus() | keyvalue; track entry.key) {
    <nz-card class="status-card" [nzBordered]="true">
      <nz-statistic [nzValue]="entry.value" [nzTitle]="entry.key"></nz-statistic>
    </nz-card>
    }
  </div>

  <nz-divider></nz-divider>

  <!-- Actions -->
  <div class="actions-row">
    <div class="action-group">
      <label>Enqueue patient</label>
      <div class="inline">
        <input nz-input placeholder="Patient UUID" [(ngModel)]="enqueuePatientId" style="width:300px" />
        <button nz-button nzType="primary" (click)="enqueue()">Enqueue</button>
      </div>
    </div>
    <div class="action-group">
      <label>Process queue</label>
      <div class="inline">
        <input nz-input type="number" [(ngModel)]="processLimit" style="width:80px" />
        <button nz-button nzType="primary" (click)="processQueue()">Run</button>
      </div>
    </div>
  </div>

  <nz-divider></nz-divider>

  <!-- Aggregates table -->
  <nz-table #aggTable [nzData]="aggregates()" [nzLoading]="loading()" [nzFrontPagination]="true" [nzPageSize]="20"
    [nzShowSizeChanger]="true" [nzPageSizeOptions]="[20, 50, 100]" nzSize="small">
    <thead>
      <tr>
        <th nzWidth="24px"></th>
        <th>Patient</th>
        <th nzWidth="80px">Ready</th>
        <th nzWidth="110px">Status</th>
        <th nzWidth="80px">Routine</th>
        <th nzWidth="150px">Updated</th>
        <th>Error</th>
        <th nzWidth="240px">Actions</th>
      </tr>
    </thead>
    <tbody>
      @for (row of aggTable.data; track row.patient_id) {
      <!-- Main row -->
      <tr>
        <td>
          @if (row.has_routine) {
          <button nz-button nzType="text" nzSize="small" nz-tooltip
            [nzTooltipTitle]="expandedPatientId() === row.patient_id ? 'Hide pipeline steps' : 'Show pipeline steps'"
            (click)="toggleSteps(row.patient_id)">
            <span nz-icon [nzType]="expandedPatientId() === row.patient_id ? 'up' : 'down'"></span>
          </button>
          }
        </td>
        <td>{{ row.patient_name ?? row.patient_id }}</td>
        <td>
          <nz-tag [nzColor]="row.is_ready ? 'green' : 'orange'">{{ row.is_ready ? 'Yes' : 'No' }}</nz-tag>
        </td>
        <td>
          <nz-tag [nzColor]="statusColor(row.routine_generation_status)">{{ row.routine_generation_status }}</nz-tag>
        </td>
        <td>
          <nz-tag [nzColor]="row.has_routine ? 'green' : 'default'">{{ row.has_routine ? 'Yes' : 'No' }}</nz-tag>
        </td>
        <td>{{ row.updated_at | date:'short' }}</td>
        <td class="error-cell">{{ row.routine_error ?? '—' }}</td>
        <td>
          <button nz-button nzSize="small" nzType="link" (click)="viewLatest(row.patient_id)">Raw</button>
          <button nz-button nzSize="small" nzType="link" (click)="requeue(row.patient_id)">Requeue</button>
          <button nz-button nzSize="small" nzType="link" nzDanger (click)="stop(row.patient_id)">Stop</button>
        </td>
      </tr>

      <!-- Expanded steps panel -->
      @if (expandedPatientId() === row.patient_id) {
      <tr class="step-panel-row">
        <td colspan="8" class="step-panel-cell">
          @if (stepDetailLoading()) {
          <div class="step-loading"><nz-spin nzSimple></nz-spin> Loading pipeline steps…</div>
          } @else {
          @if (stepDetail(); as detail) {
          <!-- Header summary -->
          <div class="step-header">
            <div class="step-meta">
              <span class="meta-item">
                <strong>Execution ID:</strong>
                <code class="exec-id" nz-tooltip [nzTooltipTitle]="detail.execution_id ?? ''">
                        {{ (detail.execution_id ?? '—').slice(0, 16) }}…
                      </code>
              </span>
              <span class="meta-item">
                <strong>Model:</strong> {{ detail.model_used ?? '—' }}
              </span>
              <span class="meta-item">
                <strong>Total time:</strong>
                {{ detail.processing_time_seconds != null ? (detail.processing_time_seconds | number:'1.1-1') + 's' :
                '—' }}
              </span>
              @if (detail.is_follow_up) {
              <nz-tag nzColor="purple">Follow-up</nz-tag>
              }
              <nz-tag [nzColor]="detail.is_valid ? 'green' : 'red'">
                {{ detail.is_valid ? '✓ Valid' : '✗ Invalid' }}
              </nz-tag>
            </div>
            <div class="step-scores">
              @if (detail.safety_score != null) {
              <span class="score-badge">
                Safety
                <nz-tag [nzColor]="confidenceColor(detail.safety_score)">{{ scorePercent(detail.safety_score)
                  }}</nz-tag>
              </span>
              }
              @if (detail.validation_score != null) {
              <span class="score-badge">
                Validation
                <nz-tag [nzColor]="confidenceColor(detail.validation_score)">{{ scorePercent(detail.validation_score)
                  }}</nz-tag>
              </span>
              }
              @if (detail.safety_retry_count > 0) {
              <nz-tag nzColor="orange">{{ detail.safety_retry_count }} safety retr{{ detail.safety_retry_count === 1 ?
                'y' : 'ies' }}</nz-tag>
              }
              @if (detail.validation_retry_count > 0) {
              <nz-tag nzColor="orange">{{ detail.validation_retry_count }} validation retr{{
                detail.validation_retry_count === 1 ? 'y' : 'ies' }}</nz-tag>
              }
            </div>
            @if (detail.errors?.length) {
            <div class="step-errors">
              @for (err of detail.errors; track $index) {
              <div class="step-error-item">⚠ {{ err }}</div>
              }
            </div>
            }
          </div>

          <!-- Step cards -->
          <nz-collapse nzGhost class="steps-collapse">
            @for (step of detail.steps; track step.node) {
            <nz-collapse-panel [nzHeader]="stepHeader" [nzActive]="false">
              <ng-template #stepHeader>
                <div class="step-card-header">
                  <span class="step-label">{{ step.label }}</span>
                  <span class="step-chips">
                    @if (step.duration_seconds != null) {
                    <nz-tag nzColor="default">⏱ {{ step.duration_seconds | number:'1.1-1' }}s</nz-tag>
                    }
                    @if (step.confidence_score != null) {
                    <nz-tag [nzColor]="confidenceColor(step.confidence_score)">
                      {{ scorePercent(step.confidence_score) }} confidence
                    </nz-tag>
                    }
                    @if (step['retry_count'] && step['retry_count'] > 0) {
                    <nz-tag nzColor="orange">{{ step['retry_count'] }} retries</nz-tag>
                    }
                    @if (step['is_valid'] === false) {
                    <nz-tag nzColor="red">Invalid</nz-tag>
                    }
                    @if (step['is_valid'] === true) {
                    <nz-tag nzColor="green">Valid</nz-tag>
                    }
                  </span>
                </div>
              </ng-template>

              <!-- Step body -->
              @if (step.reasoning) {
              <div class="step-reasoning">
                <strong>Reasoning:</strong>
                <p>{{ step.reasoning }}</p>
              </div>
              }
              @if (step['issues']?.length) {
              <div class="step-issues">
                <strong>Issues flagged:</strong>
                @for (issue of step['issues']; track $index) {
                <div class="issue-item">• {{ issue | json }}</div>
                }
              </div>
              }
              @if (step.output_summary) {
              <details class="output-details">
                <summary>Raw output</summary>
                <pre class="output-json">{{ step.output_summary | json }}</pre>
              </details>
              }
            </nz-collapse-panel>
            }
          </nz-collapse>
          }
          }
        </td>
      </tr>
      }
      } @empty {
      <tr>
        <td colspan="8" class="empty-cell">No routine aggregates found.</td>
      </tr>
      }
    </tbody>
  </nz-table>
</div>
